/**
 * Catalogue tree view — the librarian's "Goals Inventory" tab.
 *
 * `renderCatalogue` paints the full hierarchical tree (categories →
 * subcategories → objectives) with search filtering, hierarchy edit mode (the
 * librarian-only inline rename / reorder / retire / add-new
 * affordances), and the per-objective detail panel hand-off to
 * `renderCatalogueDetail`.
 *
 * Reaches back into main.ts for several still-in-main helpers
 * (renderCatalogueDetail, bindHierarchyToolbar, bindHierarchyActions,
 * commitInlineRename, saveNewCategory, saveNewSubcategory). Those move out in
 * Phase 6c.2 / 6c.3 and the import paths will redirect at that point.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6c.1.
 */

import { state } from "../state";
import { catalogueList, catalogueSearch } from "../dom";
import { escapeHtml } from "../utils/html";
import { defaultsOf, rolesLocked, shiftRoleDisplay } from "../shift-roles";
import { normaliseCatalogueQuery } from "../utils/debounce";
import type { ObservationDefinition } from "../types";
import { variantChildIds, trackableCapabilities } from "../types";
import Sortable from "sortablejs";
import { renderCatalogueDetail, recategoriseFact, makeVariantOf, unmakeVariant, beginEditSession } from "./detail";
import { showToast, showUndoToast } from "./saveModal";
import { showDeleteModal } from "./delete";
import { hasPlaceholderText, countPlaceholderGoals } from "./placeholder";
import { createVariant } from "./custom";
import { reorderFactsInBucket } from "./reorder";
import { resolveCategoryCodes, GOAL_CATEGORY_SYSTEM, GOAL_SUBCATEGORY_SYSTEM } from "./category";
import {
  trainingChip,
  isPromotedFromTraining,
  matchesProvenanceFilter,
  provenanceFilterEmptyText,
  type ProvenanceFilter,
} from "./provenance-view";
import {
  bindHierarchyToolbar,
  bindHierarchyActions,
  commitInlineRename,
  saveNewCategory,
  saveNewSubcategory,
  swapCategoryOrder,
  swapSubcategoryOrder,
  setCategoryStatus,
  setSubcategoryStatus,
} from "./hierarchy";

// OMON-107: per-browser persistence of the catalogue tree fold state.
// LocalStorage instead of Firestore because this is per-librarian-per-
// browser ergonomics, not cross-device state. Mirrors the iOS app's
// pattern at a different storage tier.
const FOLD_STATE_KEY = "omon-catalogue-fold-state-v1";
let foldStateInitialised = false;

/** Compact recording-regime label for an objective row, from its `_recordingKind`.
 * (The rows previously hardcoded "1-5 scale" for every fact regardless of type.) */
function recordingScaleLabel(od: ObservationDefinition | undefined): string {
  switch (od?._recordingKind) {
    case "boolean": return "yes / no";
    case "tally": return "tally / count";
    case "rating": return "rating / numeric";
    // OMON-204: diary (daily log) — timestamped free-text entries, not a 1–5
    // scale. Without this case a Monitored diary fact (e.g. "Daily log") fell to
    // the attainment-5 default and mislabelled as "1–5 scale" in the tree.
    case "diary": return "daily log / diary";
    case "incident": return "incident log (ABC chart)";  // OMON-232
    case "attainment-5":
    default: return "1–5 scale";
  }
}

/**
 * OMON-165: D/M/S capability badges, read by SYSTEM from the fact's category
 * codings. Used on parent, standalone AND variant rows (previously variants
 * showed no badges, so a variant's capabilities were invisible in the tree).
 */
function capBadgesHtml(od: ObservationDefinition): string {
  const caps = trackableCapabilities(od);
  if (!caps.length) return "";
  return ` <span class="cap-badges" style="font-size:11px;font-weight:700;color:#1e40af;">${
    caps.map((c) => `<span title="${c}" style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;padding:0 4px;margin-left:3px;">${c[0].toUpperCase()}</span>`).join("")
  }</span>`;
}

/**
 * ADR-0048 phase 2: the item came from training and production adopted it.
 *
 * Deliberately the QUIETEST chip in the row. ADR-0046's read side says
 * provenance is "information, not controls", and an item arriving by
 * promotion is the system working, not a problem — so this is a neutral grey
 * pill, never an alarm colour like the 🛑 mand badge below.
 *
 * The decision lives in provenance-view.trainingChip(); this only paints it.
 * It follows the tree's local inline-style convention rather than the
 * `.scope-badge` class the admin rows use, because at 11px next to the D/M/S
 * badges the admin pill's metrics look wrong — same vocabulary, row-native
 * size.
 */
function provenanceBadgeHtml(od: ObservationDefinition): string {
  const chip = trainingChip(od._provenance);
  if (!chip) return "";
  return ` <span class="prov-badge" title="${escapeHtml(chip.title)}" style="font-size:11px;font-weight:600;color:#374151;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:8px;padding:0 4px;margin-left:3px;">${escapeHtml(chip.label)}</span>`;
}

/**
 * OMON-172: ⚠ escalatable badge (matches the locked librarian mock). Rendered on
 * any fact whose librarian definition is `_escalatable`. Applied alongside the
 * D/M/S capability badges on parent, standalone AND variant rows.
 * 2026-06-10 (Ed): 🛑 mand badge added for `_mandatory`, same chip pattern.
 */
function policyBadgesHtml(od: ObservationDefinition): string {
  let badges = "";
  if (od._escalatable === true) {
    const thr = typeof od._escalationThresholdDefault === "number" ? ` &gt; ${od._escalationThresholdDefault}` : "";
    badges += ` <span class="esc-badge" title="Escalatable${thr ? ` (default threshold${thr})` : ""}" style="font-size:11px;font-weight:700;color:#92400e;background:#fde68a;border:1px solid #fde68a;border-radius:8px;padding:0 4px;margin-left:3px;">&#9888; esc</span>`;
  }
  // ADR-0035 am.8: mandatory = owed by at least one role by default; the
  // title names which (legacy `_mandatory` reads as every role).
  const owedBy = defaultsOf(od as unknown as Record<string, unknown>);
  if (owedBy.length > 0) {
    const who = owedBy.length === 4 ? "every role" : owedBy.map(shiftRoleDisplay).join(", ");
    const lock = rolesLocked(od as unknown as Record<string, unknown>) ? ", locked" : "";
    badges += ` <span class="mand-badge" title="Mandatory by default for ${escapeHtml(who)}${lock}" style="font-size:11px;font-weight:700;color:#991b1b;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:0 4px;margin-left:3px;">🛑 mand</span>`;
  }
  return badges;
}

function loadFoldStateFromStorage(allCategoryCodes: string[]): void {
  if (foldStateInitialised) return;
  foldStateInitialised = true;
  try {
    const raw = localStorage.getItem(FOLD_STATE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr) && arr.every((v) => typeof v === "string")) {
        state.collapsedSections = new Set(arr);
        return;
      }
    }
  } catch {
    // Storage unavailable (private mode, quota) or parse failure —
    // fall through to first-load default.
  }
  // First-ever load: collapse all categories. Subcategories stay
  // expanded inside (they'd be invisible anyway until the user opens
  // the parent category). Persist immediately so a refresh during
  // the same session restores the same default.
  state.collapsedSections = new Set(allCategoryCodes);
  saveFoldStateToStorage();
}

function saveFoldStateToStorage(): void {
  try {
    localStorage.setItem(
      FOLD_STATE_KEY,
      JSON.stringify([...state.collapsedSections]),
    );
  } catch {
    // Storage unavailable — fold state stays in-memory only this
    // session.
  }
}

// OMON-166 Part 2: shown in the right-hand inspector pane (Manage-Hierarchy mode)
// when no fact is selected.
const INSPECTOR_PLACEHOLDER = `
  <div style="padding:40px 16px;text-align:center;color:#6b7280;font-size:13px;">
    Select a fact on the left to view or edit it.
  </div>`;

/** The normalised search term the tree was last rendered with (null = never). */
let lastRenderedQuery: string | null = null;

/**
 * Search-box input handler: re-render only if the effective query differs
 * from what's on screen. Wired through a debounce in main.ts.
 */
export function renderCatalogueForSearch(): void {
  if (normaliseCatalogueQuery(catalogueSearch.value) === lastRenderedQuery) return;
  renderCatalogue();
}

export function renderCatalogue() {
  // OMON-166 Part 2: only the classic drill-in (non-edit mode) replaces the whole
  // pane with the detail. In Manage-Hierarchy mode we keep the tree visible on the
  // left and render the detail into a right-hand inspector pane (built below).

  // 2026-06-16 (Ed): preserve the catalogue tree's scroll position across the full
  // innerHTML rebuild. Selecting a fact (and fold toggles) re-runs renderCatalogue,
  // which replaces the scroll container with a fresh one starting at scrollTop 0 —
  // so editing several facts from a scrolled-down group kept yanking the list back
  // to the top. Capture before the rebuild; restore onto the new node after. Covers
  // both the edit-mode inner pane (#catTreeScroll) and catalogueList itself (classic
  // layout); an out-of-range restore is silently clamped by the browser.
  const prevTreeScroll = document.getElementById("catTreeScroll")?.scrollTop ?? 0;
  const prevListScroll = catalogueList.scrollTop;

  const search = normaliseCatalogueQuery(catalogueSearch.value);
  lastRenderedQuery = search;
  const isSearching = search.length > 0;

  // OMON-106: defensive multi-field substring match. Strictly additive
  // to the prior title-only check — anything that matched before still
  // matches. Adds resilience against edge cases (e.g. librarian-added
  // prefix characters like `*` confusing the title-only check, or
  // non-plain-ASCII `*` variants pasted from a different source).
  //
  // Match order: title → name (slug) → _smartGoalTemplate →
  // alpha-stripped title (drops any non-alphanumeric chars so a goal
  // titled "*Reading" matches search "reading" regardless of how the
  // asterisk character behaves under `.includes`).
  function matchesQuery(od: ObservationDefinition): boolean {
    if (!isSearching) return true;
    const title = (od.title || "").toLowerCase();
    if (title.includes(search)) return true;
    const name = (od.name || "").toLowerCase();
    if (name.includes(search)) return true;
    const smart = ((od as { _smartGoalTemplate?: string })._smartGoalTemplate || "").toLowerCase();
    if (smart.includes(search)) return true;
    const titleAlphaOnly = title.replace(/[^a-z0-9 ]+/g, "").trim();
    if (titleAlphaOnly.includes(search)) return true;
    return false;
  }

  // OMON-167: capability lens. Empty filter = show all. Otherwise a fact
  // shows if it carries ANY selected capability (OR within the lens),
  // AND-combined with the search box. A fact with several capabilities
  // (e.g. D+S) therefore appears under any selected lens.
  const capFilter = state.catalogueCapabilityFilter;
  function matchesCapability(od: ObservationDefinition): boolean {
    if (capFilter.size === 0) return true;
    return trackableCapabilities(od).some((c) => capFilter.has(c));
  }
  // 2026-07-07: a variant carries its OWN capabilities, which can differ from
  // its parent's — e.g. the Monitored tally "Pad changes" lives under the
  // Developmental parent "Toileting / continence". The lens must therefore be
  // evaluated per-variant, NOT inherited from the parent. Two consequences,
  // handled below: (1) a parent whose own caps miss the lens must still appear
  // as a container when a variant matches (else the matching variant vanishes);
  // (2) the nested render shows only the variants that match the lens (else
  // e.g. Developmental toothbrush variants leak under the M lens). Prior code
  // gated variants solely by "whichever parent survives", which lost both.
  function anyVariantMatchesCapability(od: ObservationDefinition): boolean {
    if (capFilter.size === 0) return false; // no lens ⇒ parent already matches
    return variantChildIds(od).some((vid) => {
      const v = state.catalogueData.get(vid);
      return !!v && matchesCapability(v);
    });
  }

  // 2026-06-16 (Ed): a search term that matches a category or sub-category NAME
  // reveals every fact under that group — not just facts whose own title/slug/
  // SMART-template matches. Strict superset of matchesQuery (only ever ADDS
  // results, and only while searching). Group display is resolved from the
  // hierarchy maps — the same source the tree headers render from, so it tracks
  // renames — with a fallback to the fact's own goal-category / goal-subcategory
  // coding displays for any group not present in those collections (mirrors the
  // render's odSubDisplay fallback below). The coding scan is restricted to the
  // goal-category / goal-subcategory systems so capability ("Developmental") or
  // observation-category codings don't silently over-match.
  function matchesGroup(od: ObservationDefinition, goalCode: string, subcategoryCode: string): boolean {
    if (!isSearching) return false;
    const catDisplay = (state.goalCategories.get(goalCode)?.display || "").toLowerCase();
    if (catDisplay.includes(search)) return true;
    if (subcategoryCode) {
      const subDisplay = (state.goalSubcategories.get(`${goalCode}__${subcategoryCode}`)?.display || "").toLowerCase();
      if (subDisplay.includes(search)) return true;
    }
    const cat = Array.isArray(od.category) ? od.category : [];
    for (const e of cat) {
      const coding = (e as { coding?: unknown[] })?.coding;
      if (!Array.isArray(coding)) continue;
      for (const c of coding) {
        const cc = c as { system?: string; display?: string };
        if ((cc.system === GOAL_CATEGORY_SYSTEM || cc.system === GOAL_SUBCATEGORY_SYSTEM)
            && cc.display && cc.display.toLowerCase().includes(search)) {
          return true;
        }
      }
    }
    return false;
  }

  // OMON-107: one-shot fold-state init. On first invocation (per
  // browser session), restore from LocalStorage if a prior session
  // stored it; otherwise default all current categories to collapsed.
  // Subsequent invocations are no-ops — fold state lives in
  // `state.collapsedSections` from then on.
  loadFoldStateFromStorage([...state.goalCategories.keys()]);

  // Group catalogue by Goal Category and Goal Sub-category, keyed on stable
  // codes so renaming a node in _goalCategory / _goalSubcategory doesn't
  // break grouping. Display is resolved live from the hierarchy map.
  type SubcategoryEntry = {
    subcategoryCode: string;
    subcategoryDisplay: string;
    subcategoryOrder: number;
    subcategoryStatus: string;
    objectives: Array<{ id: string; od: ObservationDefinition }>;
  };
  type GoalEntry = {
    goalCode: string;
    goalDisplay: string;
    goalOrder: number;
    goalStatus: string;
    subcategories: SubcategoryEntry[];
  };
  const grouped: GoalEntry[] = [];
  const goalOrder = new Map<string, GoalEntry>();

  // In hierarchy edit mode pre-seed the tree from the hierarchy collections so
  // that categories and subcategories with zero objectives still appear (otherwise
  // a librarian could never rename / retire an empty bucket).
  if (state.hierarchyEditMode) {
    for (const [code, cat] of state.goalCategories) {
      if (!state.hierarchyShowRetired && cat.status === "retired") continue;
      const entry: GoalEntry = {
        goalCode: code,
        goalDisplay: cat.display,
        goalOrder: cat.order,
        goalStatus: cat.status,
        subcategories: [],
      };
      goalOrder.set(code, entry);
      grouped.push(entry);
    }
    for (const [, aim] of state.goalSubcategories) {
      if (!state.hierarchyShowRetired && aim.status === "retired") continue;
      const catEntry = goalOrder.get(aim.goalCategoryCode);
      if (!catEntry) continue;
      if (catEntry.subcategories.find((a) => a.subcategoryCode === aim.code)) continue;
      catEntry.subcategories.push({
        subcategoryCode: aim.code,
        subcategoryDisplay: aim.display,
        subcategoryOrder: aim.order,
        subcategoryStatus: aim.status,
        objectives: [],
      });
    }
  }

  // Trackable v3 (ADR-0019) two-tier: collect every variant-child id so the
  // variants render NESTED under their parent fact rather than as standalone
  // siblings. We do NOT suppress while searching — variants must stay findable.
  const variantChildIdSet = new Set<string>();
  for (const [, od] of state.catalogueData) {
    for (const vid of variantChildIds(od)) variantChildIdSet.add(vid);
  }

  for (const [id, od] of state.catalogueData) {
    // Trackable v3: a variant child is rendered under its parent (below), so
    // skip placing it as a standalone objective — except when searching.
    if (!isSearching && variantChildIdSet.has(id)) continue;
    // ADR-0022 (catalogue unification, 2026-06-13): one tree, no tacked-on
    // "Monitored events" section. EVERY fact groups by its real
    // goal-category / goal-subcategory — the `observation-category:
    // monitored-event` coding is no longer a grouping discriminator (it was
    // the fossil that yanked the legacy 12 out of their functional homes into
    // a synthetic bottom bucket; develop confirmed all of them already carry a
    // real goal-category). Resolve category entries by SYSTEM URI, not index.
    const resolved = resolveCategoryCodes(od.category);
    const goalCode = resolved.goalCategory || "uncategorised";
    const subcategoryCode = resolved.goalSubcategory || "";

    // A fact survives search if its OWN text matches OR its category/sub-category
    // NAME matches (group-name search reveals all members — Ed, 2026-06-16).
    if (!matchesQuery(od) && !matchesGroup(od, goalCode, subcategoryCode)) continue;
    // Keep a parent whose own caps miss the lens if any of its variants match —
    // the variants only render nested (they're skipped as standalones above when
    // not searching), so the parent must survive to carry them. When searching,
    // variants show flattened as their own rows, so the parent isn't needed then.
    if (!matchesCapability(od) && !(!isSearching && anyVariantMatchesCapability(od))) continue;

    // ADR-0048 phase 2: the provenance lens. Unlike the capability lens
    // above, a parent is NOT kept for a matching variant's sake — provenance
    // is a property of the document itself, and a promoted parent's locally
    // authored variant genuinely is not training's work. The predicate is in
    // provenance-view.ts so it has a test; see the note in that file about
    // why the catalogue's other filters do not.
    if (!matchesProvenanceFilter(od._provenance, state.catalogueProvenanceFilter)) continue;

    // OMON-079 79f: librarian's authoring queue — when on, only show
    // goals whose carer-guidance still carries the post-reseed
    // placeholder marker. Independent of search and edit-mode filters.
    if (state.hierarchyShowOnlyPlaceholder && !hasPlaceholderText(id)) continue;

    // In edit mode + retired hidden, skip objectives whose hierarchy node is
    // retired. Applies uniformly now that monitored events live in real
    // categories (previously they were exempt as synthetic-bucket members).
    if (state.hierarchyEditMode && !state.hierarchyShowRetired) {
      const catNode = state.goalCategories.get(goalCode);
      if (catNode?.status === "retired") continue;
      const aimNode = subcategoryCode ? state.goalSubcategories.get(`${goalCode}__${subcategoryCode}`) : undefined;
      if (aimNode?.status === "retired") continue;
    }

    if (!goalOrder.has(goalCode)) {
      const catNode = state.goalCategories.get(goalCode);
      const entry: GoalEntry = {
        goalCode,
        goalDisplay: catNode?.display || `(unknown Goal Category: ${goalCode})`,
        goalOrder: catNode?.order ?? 999,
        goalStatus: catNode?.status || "active",
        subcategories: [],
      };
      goalOrder.set(goalCode, entry);
      grouped.push(entry);
    }
    const catEntry = goalOrder.get(goalCode)!;
    let aimEntry = catEntry.subcategories.find((a) => a.subcategoryCode === subcategoryCode);
    if (!aimEntry) {
      const aimNode = subcategoryCode
        ? state.goalSubcategories.get(`${goalCode}__${subcategoryCode}`)
        : undefined;
      // Safety net for any fact whose goal-subcategory code isn't in the
      // hierarchy collection: fall back to the display carried on the OD's own
      // goal-subcategory coding, then the raw code. (Generalised from the
      // former monitored-event-only path — useful for every fact.)
      let odSubDisplay: string | undefined;
      if (!aimNode && subcategoryCode) {
        const cat = Array.isArray(od.category) ? od.category : [];
        for (const e of cat) {
          const coding = (e as { coding?: unknown[] })?.coding;
          if (!Array.isArray(coding)) continue;
          for (const c of coding) {
            const cc = c as { system?: string; code?: string; display?: string };
            if (cc.system === "https://sweettreeconnect.com/fhir/CodeSystem/goal-subcategory"
                && cc.code === subcategoryCode && cc.display) {
              odSubDisplay = cc.display;
              break;
            }
          }
          if (odSubDisplay) break;
        }
      }
      aimEntry = {
        subcategoryCode,
        subcategoryDisplay: aimNode?.display
          || odSubDisplay
          || (subcategoryCode ? subcategoryCode : "(no Goal Sub-category)"),
        subcategoryOrder: aimNode?.order ?? 999,
        subcategoryStatus: aimNode?.status || "active",
        objectives: [],
      };
      catEntry.subcategories.push(aimEntry);
    }
    aimEntry.objectives.push({ id, od });
  }

  // Sort categories and subcategories by their librarian-set order (falls back to alpha).
  grouped.sort((a, b) => a.goalOrder - b.goalOrder || a.goalDisplay.localeCompare(b.goalDisplay));
  // OMON-173: sort facts WITHIN each bucket by the librarian-set `_order` (the
  // drag-to-reorder field), falling back to title. Absent `_order` sorts last
  // (Number.MAX_SAFE_INTEGER) so un-backfilled facts trail ordered ones, then
  // alphabetically — stable until the backfill seeds them.
  const ord = (o: { od: ObservationDefinition }) =>
    typeof o.od._order === "number" ? o.od._order : Number.MAX_SAFE_INTEGER;
  for (const cat of grouped) {
    cat.subcategories.sort((a, b) => a.subcategoryOrder - b.subcategoryOrder || a.subcategoryDisplay.localeCompare(b.subcategoryDisplay));
    for (const aim of cat.subcategories) {
      aim.objectives.sort((a, b) => ord(a) - ord(b) || (a.od.title || "").localeCompare(b.od.title || ""));
    }
  }

  // Build the toolbar that sits above the tree. Always shows the "Manage
  // Hierarchy" toggle so librarians can enter edit mode; the
  // unauthored-guidance filter is also always-visible (it's authoring
  // queue surface, not edit-mode chrome). Show-retired is gated on
  // edit mode.
  const placeholderCount = countPlaceholderGoals();
  // OMON-167: capability lens chips (D / M / S). Colours mirror the inspector
  // CAP pills (detail.ts CAP_META): D=purple, M=blue, S=amber. Multi-select;
  // an active chip is filled, inactive is outlined-grey. `data-cap-filter`
  // carries the capability code for the toolbar binder (hierarchy.ts).
  const CAP_CHIP_META: Record<string, { abbr: string; label: string; bg: string; fg: string }> = {
    developmental: { abbr: "D", label: "Developmental", bg: "#ddd6fe", fg: "#7c3aed" },
    monitored: { abbr: "M", label: "Monitored", bg: "#eff6ff", fg: "#1e40af" },
    safety: { abbr: "S", label: "Safety", bg: "#fde68a", fg: "#92400e" },
  };
  const capChip = (code: string) => {
    const m = CAP_CHIP_META[code];
    const on = state.catalogueCapabilityFilter.has(code);
    return `<span class="cap-filter-chip${on ? " on" : ""}" data-cap-filter="${code}" title="Filter: ${m.label}" role="button" aria-pressed="${on}" style="cursor:pointer;user-select:none;font-size:13px;font-weight:600;border:1px solid ${on ? m.fg : "#d1d5db"};border-radius:10px;padding:2px 8px;${on ? `background:${m.bg};color:${m.fg};` : "color:#6b7280;background:#fff;"}">${m.abbr}</span>`;
  };
  const capFilterChips = `
    <span style="display:flex;gap:4px;align-items:center;" title="Filter the inventory by capability">
      <span style="color:#6b7280;font-size:13px;">Lens:</span>
      ${capChip("developmental")}${capChip("monitored")}${capChip("safety")}
      ${state.catalogueCapabilityFilter.size > 0 ? `<span class="cap-filter-clear" data-cap-filter-clear="1" role="button" title="Clear capability filter" style="cursor:pointer;color:#6b7280;font-size:13px;margin-left:2px;text-decoration:underline;">clear</span>` : ""}
    </span>`;
  // ADR-0048 phase 2: the provenance lens, shown ONLY once something has
  // actually been promoted. Most planners will never see a promotion, and a
  // permanent "Source: All / Training / Production" control in a catalogue
  // with no training content is a question nobody asked — so the control
  // appears when it has something to say, and takes its count with it.
  let promotedCount = 0;
  for (const od of state.catalogueData.values()) {
    if (isPromotedFromTraining(od._provenance)) promotedCount += 1;
  }
  const provChip = (value: ProvenanceFilter, label: string, title: string) => {
    const on = state.catalogueProvenanceFilter === value;
    return `<span class="prov-filter-chip${on ? " on" : ""}" data-prov-filter="${value}" title="${escapeHtml(title)}" role="button" aria-pressed="${on}" style="cursor:pointer;user-select:none;font-size:13px;font-weight:600;border:1px solid ${on ? "#374151" : "#d1d5db"};border-radius:10px;padding:2px 8px;${on ? "background:#e5e7eb;color:#111827;" : "color:#6b7280;background:#fff;"}">${escapeHtml(label)}</span>`;
  };
  const provFilterChips = promotedCount === 0 ? "" : `
    <span style="display:flex;gap:4px;align-items:center;" title="Filter the inventory by where each item was authored">
      <span style="color:#6b7280;font-size:13px;">Source:</span>
      ${provChip("all", "All", "Everything in the catalogue")}
      ${provChip("training", `Training (${promotedCount})`, "Only items authored in training and promoted here")}
      ${provChip("local", "Production", "Only items authored in this environment")}
    </span>`;
  const toolbar = `
    <div class="hierarchy-toolbar" style="display:flex;gap:12px;align-items:center;padding:8px 12px;background:#f9fafb;border-radius:4px;margin-bottom:8px;font-size:13px;flex-wrap:wrap;">
      ${capFilterChips}
      ${provFilterChips}
      <label style="display:flex;gap:4px;align-items:center;cursor:pointer;">
        <input type="checkbox" id="toggleShowOnlyPlaceholder" ${state.hierarchyShowOnlyPlaceholder ? "checked" : ""}>
        Show only goals with unauthored guidance
      </label>
      ${placeholderCount > 0 ? `
        <span class="placeholder-count" style="background:#fffbeb;color:#b45309;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">
          ${placeholderCount} goal${placeholderCount === 1 ? "" : "s"} still ${placeholderCount === 1 ? "has" : "have"} placeholder text
        </span>
      ` : ""}
      ${state.hierarchyEditMode ? `
        <label style="display:flex;gap:4px;align-items:center;cursor:pointer;">
          <input type="checkbox" id="toggleShowRetired" ${state.hierarchyShowRetired ? "checked" : ""}>
          Show retired
        </label>
      ` : ""}
    </div>
  `;

  if (grouped.length === 0 && !state.hierarchyEditMode) {
    let emptyMessage: string;
    if (state.catalogueData.size === 0) {
      emptyMessage = "Catalogue is empty.";
    } else if (state.hierarchyShowOnlyPlaceholder) {
      emptyMessage = "No goals match the unauthored-guidance filter — every goal has authored carer-guidance content. \u{1F389}";
    } else if (state.catalogueCapabilityFilter.size > 0) {
      const lenses = [...state.catalogueCapabilityFilter].map((c) => c[0].toUpperCase() + c.slice(1)).join(" / ");
      emptyMessage = `No facts carry the selected capability (${lenses}). Clear the lens or pick another.`;
    } else if (provenanceFilterEmptyText(state.catalogueProvenanceFilter)) {
      // Say WHICH lens emptied the list — an unexplained empty catalogue
      // reads as a data loss rather than a filter.
      emptyMessage = provenanceFilterEmptyText(state.catalogueProvenanceFilter)!;
    } else {
      emptyMessage = "Try a different search term.";
    }
    catalogueList.innerHTML = toolbar + `<div class="empty-state">
      <h3>No goals found</h3>
      <p>${emptyMessage}</p>
    </div>`;
    bindHierarchyToolbar();
    return;
  }

  // OMON-166 Part 4b: category/aim row actions. The per-row icon clusters
  // (✎ ↑ ↓ ⊘) are replaced by a single ⋯ button (+ right-click) that opens a
  // context menu — same pattern as fact rows. `idx`/`total` ride on the button
  // so the menu can enable/disable Move up/down. data-stop-toggle keeps clicks
  // off the collapse handler.
  const hierMoreBtn = (kind: "category" | "aim", code: string, catCode: string, idx: number, total: number): string => {
    if (!state.hierarchyEditMode) return "";
    return `<span class="cat-hier-more" data-stop-toggle="1" data-hier-more="1" data-kind="${kind}" data-code="${escapeHtml(code)}" data-cat-code="${escapeHtml(catCode)}" data-idx="${idx}" data-total="${total}" title="Actions" style="cursor:pointer;color:#6b7280;font-weight:700;padding:0 6px;margin-left:6px;opacity:.55;">&#8943;</span>`;
  };
  const renderCategoryControls = (cat: GoalEntry, idx: number, total: number): string =>
    hierMoreBtn("category", cat.goalCode, "", idx, total);
  const renderSubcategoryControls = (cat: GoalEntry, subcategory: SubcategoryEntry, idx: number, total: number): string =>
    hierMoreBtn("aim", subcategory.subcategoryCode, cat.goalCode, idx, total);

  let treeBody = "";
  for (let ci = 0; ci < grouped.length; ci++) {
    const cat = grouped[ci];
    // Collapse state is keyed by stable code — keeps collapse/expand working across renames.
    const catKey = cat.goalCode;
    const catCollapsed = !isSearching && state.collapsedSections.has(catKey);
    const catRetired = cat.goalStatus === "retired";
    let catObjCount = 0;
    let catHtml = "";

    for (let ai = 0; ai < cat.subcategories.length; ai++) {
      const aim = cat.subcategories[ai];
      const aimKey = `${cat.goalCode}::${aim.subcategoryCode}`;
      const aimCollapsed = !isSearching && state.collapsedSections.has(aimKey);
      const aimRetired = aim.subcategoryStatus === "retired";

      catObjCount += aim.objectives.length;

      // Inline rename input replaces the label when this aim is the active inline edit target.
      const inlineEditingThisAim = state.hierarchyInlineEdit?.kind === "aim"
        && state.hierarchyInlineEdit.categoryCode === cat.goalCode
        && state.hierarchyInlineEdit.code === aim.subcategoryCode;

      let aimHtml = aim.objectives.map(({ od }) => {
        // Trackable v3: capability badges (read by SYSTEM from category).
        // ADR-0048 phase 2 appends the provenance pill last, so the badges
        // that describe what a fact IS come before the one describing where
        // it came from.
        const capBadge = capBadgesHtml(od) + policyBadgesHtml(od) + provenanceBadgeHtml(od);
        // OMON-166 Part 3: drag-grip on draggable facts (parents + standalones,
        // NOT variants — variants follow their parent). `.cat-grip` is the
        // SortableJS handle; data-stop-toggle stops it triggering select/collapse.
        const grip = state.hierarchyEditMode
          ? `<span class="cat-grip" data-stop-toggle="1" title="Drag to recategorise" style="cursor:grab;color:#d1d5db;margin-right:6px;user-select:none;">&#10295;</span>`
          : "";
        // OMON-166 Part 4: inline-rename input (double-click / context-menu Rename)
        // and the ⋯ actions button. The title span keeps a single right-group
        // sibling so the row stays a clean two-column flex (no centring).
        const inlineFact = state.hierarchyInlineEdit?.kind === "fact" && state.hierarchyInlineEdit.code === od.name;
        const titleInner = inlineFact
          ? `<input type="text" class="hier-rename-input" id="hierRenameInput" value="${escapeHtml(od.title)}" data-stop-toggle="1" data-rename-kind="fact" data-rename-code="${escapeHtml(od.name)}">`
          : `${grip}<span class="obj-name">${escapeHtml(od.title)}</span>${capBadge}`;
        const moreBtn = state.hierarchyEditMode && !inlineFact
          ? `<span class="cat-more" data-stop-toggle="1" data-obj-more="${escapeHtml(od.name)}" title="Actions" style="cursor:pointer;color:#6b7280;font-weight:700;padding:0 6px;opacity:.55;">&#8943;</span>`
          : "";
        // OMON-171: hover-revealed "+" on a parent → opens an inline draft
        // variant row inside its group. Sits left of the ⋯, edit-mode only.
        const addVariantBtn = state.hierarchyEditMode && !inlineFact
          ? `<span class="cat-add-variant" data-stop-toggle="1" data-add-variant="${escapeHtml(od.name)}" title="Add a variant under this fact" style="cursor:pointer;color:#7c3aed;font-weight:700;font-size:15px;padding:0 6px;opacity:.6;line-height:1;">&#43;</span>`
          : "";
        const isParent = od._trackableParent === true || (Array.isArray(od.hasMember) && od.hasMember.length > 0);
        if (isParent && !isSearching) {
          const variants = variantChildIds(od)
            .map((vid) => state.catalogueData.get(vid))
            .filter((v): v is ObservationDefinition => !!v)
            // Honour the capability lens per-variant: under the M lens show only
            // the Monitored variants (hide Developmental-only ones), and vice
            // versa. matchesCapability returns true for every fact when no lens
            // is active, so the default unfiltered view is unchanged.
            .filter((v) => matchesCapability(v));
          // OMON-190: variants are now draggable too — a grip lets the librarian
          // drag a variant OUT of its parent to promote it back to a standalone
          // fact (drop in a category/sub-category bucket) or re-home it under a
          // different parent (drop in another variant group). Same handle pattern
          // as standalone/parent rows; data-stop-toggle keeps grip-clicks off select.
          const vGrip = state.hierarchyEditMode
            ? `<span class="cat-grip" data-stop-toggle="1" title="Drag to promote out of this fact, or move under another" style="cursor:grab;color:#d1d5db;margin-right:6px;user-select:none;">&#10295;</span>`
            : "";
          const variantHtml = variants.map((v) => `
            <div class="objective-row objective-variant" data-obj-title="${escapeHtml(v.name)}" style="padding-left:32px;opacity:0.85;">
              <span class="objective-title">${vGrip}&#8627; <span class="obj-name">${escapeHtml(v.title)}</span>${capBadgesHtml(v)}${policyBadgesHtml(v)}${provenanceBadgeHtml(v)}</span>
              <span class="objective-scale">${recordingScaleLabel(v)} &rsaquo;</span>
            </div>`).join("");
          // OMON-171: inline draft variant row (focused empty input) when this
          // parent is the active add-variant target. Commit on Enter / non-blank
          // blur; blank blur / Escape abandons (bound below).
          const draftRow = state.hierarchyEditMode && state.hierarchyAddVariantIn === od.name
            ? `<div class="objective-row objective-variant" data-stop-toggle="1" style="padding-left:32px;">
                 <span class="objective-title" style="flex:1;">&#8627; <input type="text" id="newVariantNameInput" class="hier-rename-input" placeholder="New variant name" data-stop-toggle="1" data-add-variant-parent="${escapeHtml(od.name)}" style="width:80%;"></span>
               </div>`
            : "";
          // OMON-165: wrap parent + variants in a group container so (a) the parent
          // gets a fold chevron that collapses its variants and (b) the whole group is
          // the SortableJS drag unit — the drag highlight then covers parent+variants,
          // not just the header row. Default open (key only present when toggled shut).
          const groupKey = `grp::${od.name}`;
          const groupCollapsed = !isSearching && state.collapsedSections.has(groupKey);
          const chevron = state.hierarchyEditMode
            ? `<span class="grp-chevron" data-stop-toggle="1" data-group-chevron="${escapeHtml(groupKey)}" title="${groupCollapsed ? "Expand" : "Collapse"}" style="cursor:pointer;color:#6b7280;display:inline-block;width:12px;margin-right:2px;user-select:none;">${groupCollapsed ? "&#9656;" : "&#9662;"}</span>`
            : "";
          return `
            <div class="objective-group" data-obj-title="${escapeHtml(od.name)}">
              <div class="objective-row objective-parent" data-obj-title="${escapeHtml(od.name)}">
                <span class="objective-title">${chevron}${titleInner}</span>
                <span class="objective-row-right" style="display:flex;align-items:center;gap:8px;">${addVariantBtn}${moreBtn}<span class="objective-scale">${variants.length} variant${variants.length === 1 ? "" : "s"} &rsaquo;</span></span>
              </div>
              <div class="objective-group-body" data-parent-name="${escapeHtml(od.name)}"${groupCollapsed ? ' style="display:none;"' : ""}>${variantHtml}${draftRow}</div>
            </div>`;
        }
        return `
          <div class="objective-row" data-obj-title="${escapeHtml(od.name)}">
            <span class="objective-title">${titleInner}</span>
            <span class="objective-row-right" style="display:flex;align-items:center;gap:8px;">${moreBtn}<span class="objective-scale">${recordingScaleLabel(od)} &rsaquo;</span></span>
          </div>`;
      }).join("");

      if (aim.subcategoryCode) {
        const labelBody = inlineEditingThisAim
          ? `<input type="text" class="hier-rename-input" id="hierRenameInput"
                value="${escapeHtml(aim.subcategoryDisplay)}"
                data-stop-toggle="1"
                data-rename-kind="aim"
                data-rename-code="${escapeHtml(aim.subcategoryCode)}"
                data-rename-cat-code="${escapeHtml(cat.goalCode)}">`
          : `<span class="chevron">&#9660;</span>${escapeHtml(aim.subcategoryDisplay)}<span class="obj-count">${aim.objectives.length}</span>`;

        catHtml += `<div class="aim-label ${aimCollapsed ? "collapsed" : ""} ${aimRetired ? "is-retired" : ""}" data-section-key="${escapeHtml(aimKey)}" style="${aimRetired ? "opacity:0.5;" : ""}">
          ${labelBody}${renderSubcategoryControls(cat, aim, ai, cat.subcategories.length)}
        </div>
        <div class="aim-body ${aimCollapsed ? "collapsed" : ""}" data-cat-code="${escapeHtml(cat.goalCode)}" data-sub-code="${escapeHtml(aim.subcategoryCode)}">${aimHtml}</div>`;
      } else {
        catHtml += aimHtml;
      }
    }

    // 2026-06-11 (Ed): "Add fact…" (category ⋯ menu) inline create — the
    // tree-native replacement for the retired "+ Add Custom" form.
    if (state.hierarchyAddFactIn === cat.goalCode) {
      catHtml += `
        <div class="hier-add-form" data-stop-toggle="1" style="padding:8px 12px;background:#f9fafb;display:flex;gap:6px;align-items:center;">
          <input type="text" id="newFactNameInput" placeholder="New fact name" style="flex:1;padding:4px 6px;" data-stop-toggle="1">
          <button class="btn-primary" data-hier-action="save-new-fact" data-cat-code="${escapeHtml(cat.goalCode)}" style="padding:4px 8px;">Add</button>
          <button class="btn-outline" data-hier-action="cancel-new-fact" style="padding:4px 8px;">Cancel</button>
        </div>`;
    }

    // "Add aim" inline form appears at the end of each category in edit mode.
    if (state.hierarchyEditMode) {
      if (state.hierarchyAddSubcategoryIn === cat.goalCode) {
        catHtml += `
          <div class="hier-add-form" data-stop-toggle="1" style="padding:8px 12px;background:#f9fafb;display:flex;gap:6px;align-items:center;">
            <input type="text" id="newAimNameInput" placeholder="New sub-category name" style="flex:1;padding:4px 6px;" data-stop-toggle="1">
            <button class="btn-primary" data-hier-action="save-new-aim" data-cat-code="${escapeHtml(cat.goalCode)}" style="padding:4px 8px;">Add</button>
            <button class="btn-outline" data-hier-action="cancel-new-aim" style="padding:4px 8px;">Cancel</button>
          </div>`;
      } else {
        catHtml += `
          <div style="padding:6px 12px;">
            <button class="btn-outline hier-add-btn" data-hier-action="show-new-aim" data-cat-code="${escapeHtml(cat.goalCode)}" data-stop-toggle="1" style="font-size:13px;padding:3px 8px;">
              + Add sub-category to ${escapeHtml(cat.goalDisplay)}
            </button>
          </div>`;
      }
    }

    const inlineEditingThisCat = state.hierarchyInlineEdit?.kind === "category"
      && state.hierarchyInlineEdit.code === cat.goalCode;

    const catLabelBody = inlineEditingThisCat
      ? `<input type="text" class="hier-rename-input" id="hierRenameInput"
            value="${escapeHtml(cat.goalDisplay)}"
            data-stop-toggle="1"
            data-rename-kind="category"
            data-rename-code="${escapeHtml(cat.goalCode)}">`
      : `<span class="chevron">&#9660;</span>${escapeHtml(cat.goalDisplay)}<span class="obj-count">${catObjCount}</span>`;

    treeBody += `<div class="category-header ${catCollapsed ? "collapsed" : ""} ${catRetired ? "is-retired" : ""}" data-section-key="${escapeHtml(catKey)}" style="${catRetired ? "opacity:0.5;" : ""}">
      ${catLabelBody}${renderCategoryControls(cat, ci, grouped.length)}
    </div>
    <div class="category-body ${catCollapsed ? "collapsed" : ""}" data-cat-code="${escapeHtml(cat.goalCode)}" data-sub-code="">${catHtml}</div>`;
  }

  // "Add category" affordance at the bottom of the tree (edit mode only).
  if (state.hierarchyEditMode) {
    if (state.hierarchyShowAddCategory) {
      treeBody += `
        <div class="hier-add-form" data-stop-toggle="1" style="padding:8px 12px;margin-top:8px;background:#f9fafb;display:flex;gap:6px;align-items:center;">
          <input type="text" id="newCategoryNameInput" placeholder="New category name" style="flex:1;padding:4px 6px;" data-stop-toggle="1">
          <button class="btn-primary" data-hier-action="save-new-category" style="padding:4px 8px;">Add</button>
          <button class="btn-outline" data-hier-action="cancel-new-category" style="padding:4px 8px;">Cancel</button>
        </div>`;
    } else {
      treeBody += `
        <div style="padding:12px; text-align:center;">
          <button class="btn-outline" data-hier-action="show-new-category" data-stop-toggle="1">+ Add new category</button>
        </div>`;
    }
  }

  // OMON-166 Part 2: in Manage-Hierarchy mode, render a persistent split —
  // toolbar full-width on top, the tree on the left, a right-hand inspector pane
  // that renderCatalogueDetail() fills (or a placeholder when nothing's selected).
  // 40/60 since 2026-09-29 (Ed): the inspector holds the one-box carer guidance
  // editor beside its carer preview, and needs the width more than the tree.
  // Outside edit mode the layout is unchanged (toolbar + tree, classic drill-in).
  if (state.hierarchyEditMode) {
    catalogueList.innerHTML = toolbar + `<div style="display:flex;align-items:flex-start;gap:0;">
      <div id="catTreeScroll" style="flex:0 1 40%;min-width:0;max-height:calc(100vh - 150px);overflow:auto;padding-right:12px;border-right:1px solid #e5e7eb;">${treeBody}</div>
      <div id="catInspectorPane" style="flex:1 1 60%;min-width:0;max-height:calc(100vh - 150px);overflow:auto;padding-left:16px;">${INSPECTOR_PLACEHOLDER}</div>
    </div>`;
  } else {
    catalogueList.innerHTML = toolbar + treeBody;
  }
  // Restore the pre-rebuild scroll position onto the freshly-built scroll
  // container(s) so selection / fold re-renders keep the user's place.
  const newTreeScroll = document.getElementById("catTreeScroll");
  if (newTreeScroll) newTreeScroll.scrollTop = prevTreeScroll;
  catalogueList.scrollTop = prevListScroll;

  // 2026-07-08: in Manage-Hierarchy mode the tree lives in the #catTreeScroll
  // container (overflow:auto), centred within the wider `.content` shell — so a
  // wheel over the empty side gutters (or the toolbar) scrolls the whole PAGE
  // instead of the list, because those pixels belong to `.content`, not to the
  // scroller. Forward any such wheel into the tree so a scroll ANYWHERE in the
  // catalogue pane moves the list. Bound once on the stable `.content` ancestor
  // (it survives the frequent catalogueList re-renders); the handler resolves
  // #catTreeScroll live, so it no-ops on other panels and in classic
  // (non-split) mode where that element is absent, and it never hijacks the
  // inspector, which scrolls itself.
  //
  // 2026-07-14: "absent on other panels" was wrong — panels hide with
  // display:none (.panel/.panel.active), so #catTreeScroll stays in the DOM
  // after the catalogue first renders and this handler was eating wheel
  // events on the Plans list and plan viewer (scroll went into the hidden,
  // zero-height tree). Guard on offsetParent: it is null exactly when an
  // ancestor is display:none, i.e. the catalogue panel is not the active one.
  const contentEl = catalogueList.closest<HTMLElement>(".content");
  if (contentEl && !contentEl.dataset.catWheelBound) {
    contentEl.dataset.catWheelBound = "1";
    contentEl.addEventListener("wheel", (e) => {
      const scroller = document.getElementById("catTreeScroll");
      if (!scroller) return; // classic mode — leave native scroll alone
      if (scroller.offsetParent === null) return; // catalogue panel hidden — other panel is active
      const node = e.target as Node;
      if (scroller.contains(node)) return; // already over the tree
      const inspector = document.getElementById("catInspectorPane");
      if (inspector && inspector.contains(node)) return; // inspector self-scrolls
      // deltaMode: 0 = pixels, 1 = lines, 2 = pages — normalise to pixels.
      const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? scroller.clientHeight : 1;
      scroller.scrollTop += e.deltaY * step;
      e.preventDefault();
    }, { passive: false });
  }
  // Edit-mode 50/50: paint the selected fact into the right-hand inspector pane.
  if (state.hierarchyEditMode && state.catalogueSelectedGoal && state.catalogueData.get(state.catalogueSelectedGoal)) {
    renderCatalogueDetail();
  }
  bindHierarchyToolbar();
  bindHierarchyActions();
  // OMON-166 Part 3: enable drag-to-recategorise in Manage-Hierarchy mode.
  if (state.hierarchyEditMode) bindCatalogueDrag();

  // Bind collapse toggle. Clicks on elements marked data-stop-toggle (edit
  // controls, inline inputs, add-form buttons) do NOT toggle collapse — they
  // bubble up to the header but we ignore them here.
  catalogueList.querySelectorAll<HTMLElement>("[data-section-key]").forEach((el) => {
    el.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-stop-toggle]")) return;
      e.stopPropagation();
      const key = el.dataset.sectionKey!;
      if (state.collapsedSections.has(key)) state.collapsedSections.delete(key);
      else state.collapsedSections.add(key);
      // OMON-107: persist on every toggle so refresh / next session
      // restores the user's last fold state.
      saveFoldStateToStorage();
      renderCatalogue();
    });
  });

  // If an inline rename input is visible, focus it and bind Enter/Esc handlers.
  const renameInput = catalogueList.querySelector<HTMLInputElement>("#hierRenameInput");
  if (renameInput) {
    renameInput.focus();
    renameInput.select();
    renameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitInlineRename(renameInput);
      } else if (e.key === "Escape") {
        e.preventDefault();
        state.hierarchyInlineEdit = null;
        renderCatalogue();
      }
    });
    renameInput.addEventListener("blur", () => commitInlineRename(renameInput));
    // 2026-06-16 (Ed): live-sync left→right. While inline-renaming the fact that
    // is also open in the inspector, mirror keystrokes into the inspector's
    // title input + working copy so the right-hand heading updates as you type
    // (no save/refresh needed). Reverse direction lives in detail.ts.
    renameInput.addEventListener("input", () => {
      if (renameInput.dataset.renameKind !== "fact") return;
      if (renameInput.dataset.renameCode !== state.catalogueSelectedGoal) return;
      const editTitle = catalogueList.querySelector<HTMLInputElement>("#editTitle");
      if (editTitle) editTitle.value = renameInput.value;
      if (state.editingDetail) state.editingDetail.title = renameInput.value;
    });
  }

  // Same for new-category / new-aim inputs — Enter submits, Esc cancels.
  const newCatInput = catalogueList.querySelector<HTMLInputElement>("#newCategoryNameInput");
  if (newCatInput) {
    newCatInput.focus();
    newCatInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveNewCategory(newCatInput.value);
      } else if (e.key === "Escape") {
        state.hierarchyShowAddCategory = false;
        renderCatalogue();
      }
    });
  }
  const newAimInput = catalogueList.querySelector<HTMLInputElement>("#newAimNameInput");
  if (newAimInput) {
    newAimInput.focus();
    newAimInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveNewSubcategory(state.hierarchyAddSubcategoryIn!, newAimInput.value);
      } else if (e.key === "Escape") {
        state.hierarchyAddSubcategoryIn = null;
        renderCatalogue();
      }
    });
  }

  // OMON-171: inline draft-variant input. Focus on render; Enter / non-blank
  // blur commits (createVariant); Escape / blank blur abandons. A `done` latch
  // stops the blur handler double-firing after an Enter commit.
  const newVariantInput = catalogueList.querySelector<HTMLInputElement>("#newVariantNameInput");
  if (newVariantInput) {
    newVariantInput.focus();
    let done = false;
    const parentName = newVariantInput.dataset.addVariantParent!;
    const commit = async () => {
      if (done) return;
      done = true;
      const ok = await createVariant(parentName, newVariantInput.value);
      // On success or abandon (blank/dupe) we close the draft and repaint. On a
      // *transient* failure createVariant returns false too, but it has already
      // surfaced the error in statusEl; closing the draft is the safe default
      // (the librarian retries) rather than trapping focus in a dead input.
      state.hierarchyAddVariantIn = null;
      if (ok) {
        // Keep the parent's group expanded so the new variant is visible.
        state.collapsedSections.delete(`grp::${parentName}`);
      }
      renderCatalogue();
    };
    newVariantInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void commit();
      } else if (e.key === "Escape") {
        done = true;
        state.hierarchyAddVariantIn = null;
        renderCatalogue();
      }
    });
    newVariantInput.addEventListener("blur", () => void commit());
  }

  // Bind objective click → detail
  catalogueList.querySelectorAll<HTMLElement>(".objective-row[data-obj-title]").forEach((row) => {
    row.addEventListener("click", (e) => {
      // OMON-166 Part 4: clicks on the grip, ⋯ button, or inline-rename input
      // (all data-stop-toggle) must not select/re-render — that would destroy
      // the rename input mid-edit or swallow the menu click.
      if ((e.target as HTMLElement).closest("[data-stop-toggle]")) return;
      e.stopPropagation();
      beginEditSession(row.dataset.objTitle!);  // OMON-232: single atomic fact switch
      state.catalogueEditMode = false;
      state.catalogueDetailTab = "attainment";
      state.catalogueMonitoredTab = "prompt";
      renderCatalogue();
    });
  });

  // OMON-166 Part 4: fact-row actions (edit mode only). Double-click the title
  // to rename in place; ⋯ button or right-click opens the context menu. Variants
  // are excluded (renamed/moved via their parent; their row carries no grip/⋯).
  if (state.hierarchyEditMode) {
    catalogueList.querySelectorAll<HTMLElement>(".objective-row:not(.objective-variant)[data-obj-title]").forEach((row) => {
      const name = row.dataset.objTitle!;
      const titleEl = row.querySelector<HTMLElement>(".objective-title");
      titleEl?.addEventListener("dblclick", (e) => {
        e.stopPropagation();
        if ((e.target as HTMLElement).closest("[data-stop-toggle]")) return; // not the grip
        state.hierarchyInlineEdit = { kind: "fact", code: name };
        renderCatalogue();
      });
      row.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        openFactContextMenu(name, e.clientX, e.clientY);
      });
      row.querySelector<HTMLElement>(".cat-more")?.addEventListener("click", (e) => {
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        openFactContextMenu(name, r.left, r.bottom + 4);
      });
      // OMON-171: "+" → open the inline draft-variant row under this parent.
      row.querySelector<HTMLElement>(".cat-add-variant")?.addEventListener("click", (e) => {
        e.stopPropagation();
        state.hierarchyAddVariantIn = name;
        renderCatalogue();
      });
    });

    // OMON-166 Part 4b: category/aim header actions via the ⋯ button + right-click
    // (replaces the old ✎ ↑ ↓ ⊘ icon clusters). No double-click rename on headers
    // — that would fight the click-to-collapse; Rename lives in the menu instead.
    catalogueList.querySelectorAll<HTMLElement>(".cat-hier-more[data-hier-more]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        openHierarchyContextMenu(btn, r.left, r.bottom + 4);
      });
    });
    catalogueList.querySelectorAll<HTMLElement>(".category-header, .aim-label").forEach((header) => {
      header.addEventListener("contextmenu", (e) => {
        const btn = header.querySelector<HTMLElement>(".cat-hier-more[data-hier-more]");
        if (!btn) return;
        e.preventDefault();
        openHierarchyContextMenu(btn, e.clientX, e.clientY);
      });
    });

    // OMON-165: parent-group fold chevron — collapse/expand a parent's variants.
    // Keyed in state.collapsedSections (persisted like category/aim folds); the
    // chevron is data-stop-toggle so it doesn't trigger the parent row's select.
    catalogueList.querySelectorAll<HTMLElement>(".grp-chevron[data-group-chevron]").forEach((ch) => {
      ch.addEventListener("click", (e) => {
        e.stopPropagation();
        const key = ch.dataset.groupChevron!;
        if (state.collapsedSections.has(key)) state.collapsedSections.delete(key);
        else state.collapsedSections.add(key);
        saveFoldStateToStorage();
        renderCatalogue();
      });
    });
  }
}

/**
 * OMON-166 Part 3: drag-to-recategorise. Every `.category-body` and `.aim-body`
 * becomes a SortableJS drop zone in one shared group, so a fact dragged by its
 * `.cat-grip` handle can be moved into another category (`.category-body`,
 * sub-category cleared) or a sub-category grouping (`.aim-body`). Variants
 * aren't independently draggable (`draggable` excludes `.objective-variant`) —
 * moving a parent aligns its variants to the same category.
 *
 * The live `onSnapshot` catalogue listeners are suppressed mid-drag via
 * `state.catalogueDragging` (set in `onStart`, reset in `onEnd`'s `finally`) so
 * neither another librarian's edit nor our own recategorise write repaints the
 * tree out from under the active drag. `onEnd` always renders from truth, which
 * reverts a no-op/failed move and lands a successful one under its new bucket.
 *
 * OMON-173: intra-bucket order IS now persisted — facts carry `_order` and a
 * same-bucket drop renumbers the bucket (`reorderFactsInBucket`) instead of
 * reverting. A cross-bucket drop recategorises then assigns order at the drop
 * position. The tree sorts each bucket's facts by `_order` (fallback: title).
 */
function bindCatalogueDrag(): void {
  const containers = catalogueList.querySelectorAll<HTMLElement>(".category-body, .aim-body, .objective-group-body");
  containers.forEach((el) => {
    Sortable.create(el, {
      group: "catalogue-facts",
      handle: ".cat-grip",
      // OMON-165 / OMON-190: the draggable units are the whole parent GROUP
      // (.objective-group wraps parent+variants), a standalone fact row, AND
      // (new in OMON-190) an individual variant row. The parent row itself
      // (.objective-parent, nested in a group) still isn't dragged alone —
      // dragging the group moves the parent and its variants as one. Variants
      // ARE now dragged individually so they can be promoted out / re-homed.
      draggable: ".objective-group, .objective-row:not(.objective-parent)",
      animation: 150,
      ghostClass: "sortable-ghost",
      // OMON-166 Part 3: mouse-event drag (not native HTML5 DnD). Gives a
      // consistent styled ghost across browsers, and `fallbackTolerance`
      // keeps a small click-jitter on the grip from starting a phantom drag
      // (so grip-click still selects the fact).
      forceFallback: true,
      fallbackTolerance: 4,
      onStart: () => { state.catalogueDragging = true; clearDropHighlight(); },
      // OMON-190: live insertion feedback. The dashed maroon ghost (.sortable-ghost,
      // styled in the template) already marks the exact slot; this highlights the
      // CONTAINER the drop will land in so the librarian sees which bucket — and a
      // variant group-body lights up maroon ("make a variant") vs a category/aim
      // bucket's lighter tint ("place as a standalone fact"). Returning true never
      // cancels the move; it's purely a hover indicator.
      onMove: (evt) => {
        clearDropHighlight();
        const target = evt.to as HTMLElement | null;
        if (target) target.classList.add("cat-drop-target");
        return true;
      },
      onEnd: async (evt) => {
        try {
          const item = evt.item as HTMLElement;
          const name = item.dataset.objTitle;
          if (!name) return;
          const to = evt.to as HTMLElement;
          const from = evt.from as HTMLElement;
          const destCat = to.dataset.catCode || "";
          const destSub = to.dataset.subCode || "";
          const srcCat = from.dataset.catCode || "";
          const srcSub = from.dataset.subCode || "";
          const od = state.catalogueData.get(name);
          const title = od?.title || name;

          // OMON-173: read the destination bucket's NEW order from the DOM (SortableJS
          // has already placed the dragged unit). Only the top-level draggable units —
          // parent GROUPS and standalone rows — carry the bucket order; nested variant
          // rows are excluded. (Hoisted above the variant branch in OMON-190 so the
          // promote path can reuse it.)
          const readBucketOrder = (container: HTMLElement): string[] =>
            Array.from(container.querySelectorAll<HTMLElement>(
              ":scope > .objective-group, :scope > .objective-row:not(.objective-parent):not(.objective-variant)",
            )).map((e) => e.dataset.objTitle || "").filter(Boolean);

          // OMON-190: the dragged unit is an existing VARIANT (it lives inside a
          // parent's variant group). Dragging it OUT promotes it back to a standalone
          // fact; dragging it into a DIFFERENT variant group re-homes it under that
          // parent. `unmakeVariant` (built in OMON-188 as the drag-in Undo) is the
          // engine — it clears _variantOf, drops the old parent's hasMember edge, and
          // (when given a category) recategorises. The source group-body carries the
          // old parent in data-parent-name.
          const isVariant = od?._variantOf != null || item.classList.contains("objective-variant");
          if (isVariant) {
            const oldParent = from.dataset.parentName || "";
            const oldParentTitle = state.catalogueData.get(oldParent)?.title || oldParent;
            // Re-home: dropped into another parent's variant group.
            if (to.classList.contains("objective-group-body")) {
              const newParent = to.dataset.parentName || "";
              if (!newParent || newParent === oldParent || newParent === name) return; // same group / self → no-op
              // Detach from the old parent (no recategorise yet), then attach to the
              // new one (makeVariantOf aligns the child's category to the new parent).
              const detached = await unmakeVariant(name, oldParent, "", "");
              if (!detached) { showToast(`Couldn't move "${title}"`, "error"); return; }
              const attached = await makeVariantOf(name, newParent);
              if (!attached) {
                await makeVariantOf(name, oldParent); // roll back so it isn't orphaned
                showToast(`Couldn't move "${title}" under that fact`, "error");
                return;
              }
              const newParentTitle = state.catalogueData.get(newParent)?.title || newParent;
              showUndoToast(`Moved "${title}" under "${newParentTitle}"`, () => {
                void (async () => {
                  await unmakeVariant(name, newParent, "", "");
                  await makeVariantOf(name, oldParent);
                  renderCatalogue();
                })();
              });
              return;
            }
            // Promote: dropped into a category / sub-category bucket → standalone fact
            // at that level (sub-category if the bucket has one, else category-level —
            // i.e. "up one or more levels" follows the drop target).
            const promoted = await unmakeVariant(name, oldParent, destCat, destSub);
            if (!promoted) { showToast(`Couldn't promote "${title}"`, "error"); return; }
            // Keep the inspector working copy in sync (editingDetail owns category at
            // save time — a stale value would revert the move on next autosave).
            if (name === state.catalogueSelectedGoal && state.editingDetail) {
              state.editingDetail.categoryCode = destCat;
              state.editingDetail.subcategoryCode = destSub;
            }
            await reorderFactsInBucket(readBucketOrder(to));
            const destName = destSub
              ? (state.goalSubcategories.get(`${destCat}__${destSub}`)?.display || destSub)
              : (state.goalCategories.get(destCat)?.display || destCat);
            showUndoToast(`Promoted "${title}" out of "${oldParentTitle}" into ${destName}`, () => {
              void (async () => {
                await makeVariantOf(name, oldParent); // re-nest restores _variantOf + category
                if (name === state.catalogueSelectedGoal && state.editingDetail) {
                  const pcc = resolveCategoryCodes(state.catalogueData.get(oldParent)?.category);
                  state.editingDetail.categoryCode = pcc.goalCategory || state.editingDetail.categoryCode;
                  state.editingDetail.subcategoryCode = pcc.goalSubcategory || "";
                }
                renderCatalogue();
              })();
            });
            return;
          }

          // OMON-188: dropped INTO a parent fact's variant group → make it a
          // variant of that parent (re-wires _variantOf + parent.hasMember).
          if (to.classList.contains("objective-group-body")) {
            const parentName = to.dataset.parentName;
            if (!parentName || parentName === name) return;
            const ok = await makeVariantOf(name, parentName);
            if (!ok) { showToast(`Couldn't nest "${title}" under that fact`, "error"); return; }
            // Keep the inspector working copy in sync (editingDetail owns category
            // at save time — a stale value would revert the move on next autosave).
            if (name === state.catalogueSelectedGoal && state.editingDetail) {
              const pcc = resolveCategoryCodes(state.catalogueData.get(parentName)?.category);
              state.editingDetail.categoryCode = pcc.goalCategory || state.editingDetail.categoryCode;
              state.editingDetail.subcategoryCode = pcc.goalSubcategory || "";
            }
            const parentTitle = state.catalogueData.get(parentName)?.title || parentName;
            showUndoToast(`Made "${title}" a variant of "${parentTitle}"`, () => {
              void (async () => {
                await unmakeVariant(name, parentName, srcCat, srcSub);
                if (name === state.catalogueSelectedGoal && state.editingDetail) {
                  state.editingDetail.categoryCode = srcCat;
                  state.editingDetail.subcategoryCode = srcSub;
                }
                renderCatalogue();
              })();
            });
            return;
          }
          // Same bucket → pure reorder. Persist the new `_order` and let the finally's
          // render land it (previously this was a no-op that snapped back — OMON-173).
          if (destCat === srcCat && destSub === srcSub) {
            await reorderFactsInBucket(readBucketOrder(to));
            return;
          }
          // Cross-bucket → recategorise (OMON-166 Part 3), then assign order at the
          // destination from the drop position so the moved fact lands where dropped.
          const ok = await recategoriseFact(name, destCat, destSub);
          if (!ok) {
            showToast(`Couldn't move "${title}" there`, "error");
            return;
          }
          // OMON-188: keep the inspector working copy in sync (see makeVariant branch).
          if (name === state.catalogueSelectedGoal && state.editingDetail) {
            state.editingDetail.categoryCode = destCat;
            state.editingDetail.subcategoryCode = destSub;
          }
          // Parent move: align variant children to the same category (no sub).
          if (od) {
            for (const vid of variantChildIds(od)) {
              await recategoriseFact(vid, destCat, "");
            }
          }
          await reorderFactsInBucket(readBucketOrder(to));
          const destName = state.goalCategories.get(destCat)?.display || destCat;
          // OMON-182: drag is the easiest edit to make by accident (touchpad
          // slip) — the toast carries an Undo that moves the fact (and its
          // variants) straight back to the source bucket.
          showUndoToast(`Moved "${title}" to ${destName}`, () => {
            void (async () => {
              const back = await recategoriseFact(name, srcCat, srcSub);
              if (!back) { showToast(`Couldn't move "${title}" back`, "error"); return; }
              if (name === state.catalogueSelectedGoal && state.editingDetail) {
                state.editingDetail.categoryCode = srcCat;
                state.editingDetail.subcategoryCode = srcSub;
              }
              if (od) {
                for (const vid of variantChildIds(od)) {
                  await recategoriseFact(vid, srcCat, "");
                }
              }
              renderCatalogue();
            })();
          });
        } finally {
          state.catalogueDragging = false;
          clearDropHighlight();
          renderCatalogue();
        }
      },
    });
  });
}

/** OMON-190: clear the drag drop-target highlight from every catalogue bucket. */
function clearDropHighlight(): void {
  catalogueList
    .querySelectorAll<HTMLElement>(".cat-drop-target")
    .forEach((e) => e.classList.remove("cat-drop-target"));
}

/**
 * OMON-166 Part 4: fact-row context menu (right-click or the ⋯ button). Items
 * reuse the existing logic — Rename triggers the in-place inline rename
 * (`state.hierarchyInlineEdit` kind "fact"), Move to category ▸ opens an inline
 * submenu of categories and uses the same `recategoriseFact` + variant-alignment
 * path as drag, and Delete… opens the existing soft-delete confirm modal. The
 * menu is appended to `document.body` (so it isn't clipped by the scroll panes),
 * clamped to the viewport, and dismissed on outside-click / Escape / scroll.
 */
function openFactContextMenu(name: string, x: number, y: number): void {
  closeFactContextMenu();
  const od = state.catalogueData.get(name);
  if (!od) return;
  const title = od.title || name;
  const currentCat = resolveCategoryCodes(od.category).goalCategory || "";

  const menu = document.createElement("div");
  menu.id = "catFactMenu";
  menu.style.cssText =
    "position:fixed;z-index:1000;background:#fff;border:1px solid #e5e7eb;border-radius:9px;" +
    "box-shadow:0 8px 30px rgba(0,0,0,.16);padding:4px;min-width:190px;font-size:13px;";
  const mi = (label: string, attr: string, danger = false) =>
    `<div class="cfm-item" ${attr} style="padding:6px 8px;border-radius:6px;cursor:pointer;display:flex;justify-content:space-between;gap:16px;${danger ? "color:#dc2626;" : ""}">${label}</div>`;

  const renderMain = () => {
    menu.innerHTML =
      mi("Rename", `data-act="rename"`) +
      mi('Move to category <span style="color:#6b7280;">&#9656;</span>', `data-act="move"`) +
      `<div style="height:1px;background:#e5e7eb;margin:4px 4px;"></div>` +
      mi("Delete…", `data-act="delete"`, true);
    menu.querySelectorAll<HTMLElement>(".cfm-item").forEach((el) => {
      el.addEventListener("mouseenter", () => (el.style.background = "#f5f3ff"));
      el.addEventListener("mouseleave", () => (el.style.background = ""));
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        const act = el.dataset.act;
        if (act === "rename") {
          closeFactContextMenu();
          state.hierarchyInlineEdit = { kind: "fact", code: name };
          renderCatalogue();
        } else if (act === "move") {
          renderMove();
        } else if (act === "delete") {
          closeFactContextMenu();
          showDeleteModal(name, title);
        }
      });
    });
  };

  const renderMove = () => {
    const cats = [...state.goalCategories.values()]
      .filter((c) => c.status !== "retired" && c.code !== currentCat)
      .sort((a, b) => a.order - b.order || a.display.localeCompare(b.display));
    const back = `<div class="cfm-item" data-back="1" style="padding:6px 8px;border-radius:6px;cursor:pointer;color:#6b7280;">&#9666; Back</div>`;
    const list = cats.length
      ? cats.map((c) => mi(escapeHtml(c.display), `data-move-to="${escapeHtml(c.code)}"`)).join("")
      : `<div style="padding:6px 8px;color:#6b7280;">No other categories</div>`;
    menu.innerHTML = back + `<div style="height:1px;background:#e5e7eb;margin:4px 4px;"></div>` +
      `<div style="max-height:280px;overflow:auto;">${list}</div>`;
    menu.querySelectorAll<HTMLElement>(".cfm-item").forEach((el) => {
      el.addEventListener("mouseenter", () => (el.style.background = "#f5f3ff"));
      el.addEventListener("mouseleave", () => (el.style.background = ""));
      el.addEventListener("click", async (e) => {
        e.stopPropagation();
        if (el.dataset.back) { renderMain(); return; }
        const destCat = el.dataset.moveTo;
        if (!destCat) return;
        closeFactContextMenu();
        const ok = await recategoriseFact(name, destCat, "");
        if (!ok) { showToast(`Couldn't move "${title}" there`, "error"); return; }
        for (const vid of variantChildIds(od)) await recategoriseFact(vid, destCat, "");
        showToast(`Moved "${title}" to ${state.goalCategories.get(destCat)?.display || destCat}`, "success");
        renderCatalogue();
      });
    });
  };

  document.body.appendChild(menu);
  renderMain();
  // Clamp to viewport now that the menu has a measured size.
  const rect = menu.getBoundingClientRect();
  menu.style.left = `${Math.min(x, window.innerWidth - rect.width - 8)}px`;
  menu.style.top = `${Math.min(y, window.innerHeight - rect.height - 8)}px`;

  // Dismissers bound on the next tick so the opening click doesn't immediately close it.
  setTimeout(() => {
    // Capture-phase pointerdown: tree row handlers stopPropagation() on click,
    // which starved a bubbling document listener — the menu only dismissed on
    // Escape (Ed, 2026-06-10). Capture fires top-down before any of that, and
    // pointerdown matches native menus (dismiss on press, not release).
    document.addEventListener("pointerdown", onDocClickClose, true);
    document.addEventListener("keydown", onKeyClose);
    catalogueList.addEventListener("scroll", closeFactContextMenu, { once: true, capture: true });
  }, 0);
}

function onDocClickClose(e: PointerEvent | MouseEvent) {
  const menu = document.getElementById("catFactMenu");
  if (menu && !menu.contains(e.target as Node)) closeFactContextMenu();
}
function onKeyClose(e: KeyboardEvent) {
  if (e.key === "Escape") closeFactContextMenu();
}
function closeFactContextMenu(): void {
  document.getElementById("catFactMenu")?.remove();
  document.removeEventListener("pointerdown", onDocClickClose, true);
  document.removeEventListener("keydown", onKeyClose);
}

/**
 * OMON-166 Part 4b: category/aim header context menu (⋯ button + right-click),
 * replacing the old per-row ✎ ↑ ↓ ⊘ icon clusters. Items delegate to the
 * existing hierarchy CRUD (swap order / set status / inline rename / add aim) —
 * those functions re-render themselves. Reuses the fact-menu's id + dismiss
 * helpers (only one menu open at a time).
 */
function openHierarchyContextMenu(btn: HTMLElement, x: number, y: number): void {
  closeFactContextMenu();
  const kind = btn.dataset.kind as "category" | "aim";
  const code = btn.dataset.code || "";
  const catCode = btn.dataset.catCode || "";
  const idx = Number(btn.dataset.idx || "0");
  const total = Number(btn.dataset.total || "0");
  const retired = kind === "category"
    ? state.goalCategories.get(code)?.status === "retired"
    : state.goalSubcategories.get(`${catCode}__${code}`)?.status === "retired";

  const menu = document.createElement("div");
  menu.id = "catFactMenu";
  menu.style.cssText =
    "position:fixed;z-index:1000;background:#fff;border:1px solid #e5e7eb;border-radius:9px;" +
    "box-shadow:0 8px 30px rgba(0,0,0,.16);padding:4px;min-width:190px;font-size:13px;";
  const item = (label: string, act: string, opts: { disabled?: boolean } = {}) =>
    `<div class="cfm-item" data-act="${act}" ${opts.disabled ? 'data-disabled="1"' : ""} style="padding:6px 8px;border-radius:6px;cursor:${opts.disabled ? "default" : "pointer"};${opts.disabled ? "color:#d1d5db;" : ""}">${label}</div>`;

  menu.innerHTML =
    item("Rename", "rename") +
    item("Move up", "up", { disabled: idx === 0 }) +
    item("Move down", "down", { disabled: idx >= total - 1 }) +
    (kind === "category" ? item("Add fact…", "add-fact") + item("Add sub-category…", "add-aim") : "") +
    `<div style="height:1px;background:#e5e7eb;margin:4px 4px;"></div>` +
    item(retired ? "Restore" : "Retire", "toggle-status");

  menu.querySelectorAll<HTMLElement>(".cfm-item").forEach((el) => {
    if (el.dataset.disabled) return;
    el.addEventListener("mouseenter", () => (el.style.background = "#f5f3ff"));
    el.addEventListener("mouseleave", () => (el.style.background = ""));
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      const act = el.dataset.act;
      closeFactContextMenu();
      if (act === "add-fact") {
        state.hierarchyAddFactIn = code;
        renderCatalogue();
      } else if (act === "rename") {
        state.hierarchyInlineEdit = kind === "aim"
          ? { kind: "aim", code, categoryCode: catCode }
          : { kind: "category", code };
        renderCatalogue();
      } else if (act === "up" || act === "down") {
        const dir = act === "up" ? -1 : 1;
        if (kind === "category") void swapCategoryOrder(code, dir);
        else void swapSubcategoryOrder(catCode, code, dir);
      } else if (act === "toggle-status") {
        const next = retired ? "active" : "retired";
        if (kind === "category") void setCategoryStatus(code, next);
        else void setSubcategoryStatus(catCode, code, next);
      } else if (act === "add-aim") {
        state.hierarchyAddSubcategoryIn = code;
        renderCatalogue();
      }
    });
  });

  document.body.appendChild(menu);
  const rect = menu.getBoundingClientRect();
  menu.style.left = `${Math.min(x, window.innerWidth - rect.width - 8)}px`;
  menu.style.top = `${Math.min(y, window.innerHeight - rect.height - 8)}px`;
  setTimeout(() => {
    // Capture-phase pointerdown: tree row handlers stopPropagation() on click,
    // which starved a bubbling document listener — the menu only dismissed on
    // Escape (Ed, 2026-06-10). Capture fires top-down before any of that, and
    // pointerdown matches native menus (dismiss on press, not release).
    document.addEventListener("pointerdown", onDocClickClose, true);
    document.addEventListener("keydown", onKeyClose);
    catalogueList.addEventListener("scroll", closeFactContextMenu, { once: true, capture: true });
  }, 0);
}
